import React from "react";
import PropTypes from "prop-types";
import RSButton from "reactstrap/lib/Button";
import Spinner from "reactstrap/lib/Spinner";

import { gatherClasses, gatherConditionalProps } from "../../lib/utils";
import Bouncer from "../bouncer/bouncer";
import Icon from "../icon/icon";
import "./button.scss";

const renderBouncer = ({ isLoading }) =>
  isLoading ? <Bouncer size="md" className="oui-btn-loading-bouncer" /> : null;

const renderButtonContent = ({ type, icon, label, isSubmitting }) => {
  const iconComp = icon ? (
    React.isValidElement(icon) ? (
      React.cloneElement(icon, {
        key: "button-icon",
        className: label
          ? gatherClasses(null, "oui-mr-2", icon.props.className)
          : icon.props.className
      })
    ) : (
      <Icon muted={false} {...icon} key="button-icon" />
    )
  ) : null;

  const labelComp = label ? (
    <span className="oui-btn-label" key="button-label">
      {label}
    </span>
  ) : null;

  const spinnerComp =
    type === "primary" && isSubmitting ? (
      <Spinner
        className="submit-spinner"
        color="light"
        size="sm"
        key="button-spinner"
      />
    ) : null;

  return [iconComp, labelComp, spinnerComp];
};

/**
 * Button is the most common component of any UI
 * It is used to trigger some action like submit or reset etc
 * It has couple of states like active, disabled
 * and types like primary, secondary and link button
 */
const Button = props => {
  /**
   * If button type is link then only honour given href
   * so that the button is created using <a>
   */
  const {
    type,
    href: propHref,
    dataTestId,
    active,
    block,
    disabled,
    isSubmitting,
    label,
    ariaLabel,
    isLoading,
    isSuccess,
    target
  } = props;
  const href = type !== "link" ? null : propHref;

  let btnType =
    type === "link" ? "link" : type === "danger" ? "danger" : "primary";

  if (isSuccess) {
    btnType = "success";
  }

  return (
    <RSButton
      data-testid={dataTestId}
      active={active}
      block={block}
      className={gatherClasses(props, isLoading && "oui-btn-loading")}
      color={btnType}
      disabled={disabled || isSubmitting}
      href={href}
      target={href !== null ? target : undefined}
      outline={type === "secondary"}
      {...gatherConditionalProps(
        props,
        "size",
        "onClick",
        "onMouseUp",
        "onMouseDown"
      )}
      aria-label={label ? "" : ariaLabel}
    >
      {isLoading && renderBouncer(props)}
      {renderButtonContent(props)}
    </RSButton>
  );
};

Button.defaultProps = {
  type: "secondary",
  href: null,
  size: "md",
  disabled: false,
  block: false,
  dataTestId: "orion-button"
};

Button.displayName = "OrionButton";

Button.propTypes = {
  /**
   * label: the label of the button
   */
  label: PropTypes.string,
  /**
   * type: type of the button. Any one from the given list
   * Default is secondary
   */
  type: PropTypes.oneOf(["primary", "secondary", "link", "danger", "success"]),
  /**
   * href: href is required for link button
   * If href is provided then the button is created using <a> else using <button>
   */
  href: PropTypes.string,
  /**
   * size: button size.
   * Default is md
   */
  size: PropTypes.oneOf(["lg", "md", "sm"]),
  /**
   * disabled: button in the disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * onClick: button click handler function
   */
  onClick: PropTypes.func,
  /**
   * block: full width button
   */
  target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"]),
  block: PropTypes.bool,
  isSuccess: PropTypes.bool,
  className: PropTypes.string,
  active: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.shape({
      src: PropTypes.string,
      size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
      width: PropTypes.string,
      height: PropTypes.string,
      classes: PropTypes.string,
      onClick: PropTypes.func
    }),
    PropTypes.node
  ]),
  dataTestId: PropTypes.string,
  isSubmitting: PropTypes.bool,
  ariaLabel: PropTypes.string,
  isLoading: PropTypes.bool
};

export default Button;
